import React from 'react';

interface Player {
  number: number;
  name: string;
}

interface Penalty {
  id: string;
  label: string;
  time: string;
  player: Player;
  periodLabel?: string; // NEW: Optional period indicator like "(from Q1)"
  cardColor?: 'yellow' | 'red' | null;
}

interface PenaltiesSectionProps {
  usPenalties: Penalty[];
  themPenalties: Penalty[];
  onDeletePenalty: (side: 'US' | 'THEM', id: string) => void;
  onEndPenalty: (side: 'US' | 'THEM', id: string) => void;
  onAddPenalty: (side: 'US' | 'THEM') => void;
  submittingActionKey?: string | null;
  redCardMinutes?: number;
}

function formatTime(time: string): string {
  // If already in mm:ss or hh:mm:ss, return as is
  if (time.includes(':')) return time;
  const totalSeconds = parseInt(time, 10);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;

  return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

const CARD_STYLES = {
  yellow: { card: 'bg-[#f6e27a] text-sd-ink', button: 'hover:bg-[#e0c642]' },
  red: { card: 'bg-[#b71c1c] text-white', button: 'hover:bg-[#b71c1c]' },
};

const PenaltyCard: React.FC<{
  penalty: Penalty;
  side: 'US' | 'THEM';
  redCardMinutes: number;
  submittingActionKey: string | null;
  onDeletePenalty: (side: 'US' | 'THEM', id: string) => void;
  onEndPenalty: (side: 'US' | 'THEM', id: string) => void;
}> = ({ penalty, side, redCardMinutes, submittingActionKey, onDeletePenalty, onEndPenalty }) => {
  const isRed = penalty.cardColor === 'red';
  const style = CARD_STYLES[penalty.cardColor === 'yellow' ? 'yellow' : 'red'];
  const deleteKey = `penalty-delete-${side}-${penalty.id}`;
  const endKey = `penalty-end-${side}-${penalty.id}`;

  return (
    <div className={`${style.card} rounded-lg shadow-lg px-4 py-3 flex flex-col w-full relative animate-fade-in`}>
      <div className="text-xs font-bold tracking-wide uppercase">
        {penalty.cardColor === 'yellow' ? 'Yellow Card' : isRed ? 'Red Card' : 'Card'} · Card Penalty Active
      </div>
      {isRed && (
        <div className="text-xs font-bold tracking-wide uppercase mt-0.5">
          {redCardMinutes}-min {side === 'US' ? 'Man Down' : 'Man Up'} ·{' '}
          {side === 'US' ? 'Player Ejected' : 'Opponent Ejected'}
        </div>
      )}
      <div className="font-bold text-base mt-1">{penalty.label}</div>
      <div className="text-base flex items-center justify-between gap-2">
        {formatTime(penalty.time)}
        {penalty.periodLabel && <div className="font-bold opacity-90">{penalty.periodLabel}</div>}
      </div>
      <div className="flex items-center justify-between gap-2 mt-1">
        <button
          className={`bg-gray-300 text-black rounded px-3 py-1 font-semibold transition-colors ${
            submittingActionKey === deleteKey ? 'cursor-not-allowed opacity-60' : 'hover:bg-gray-400'
          }`}
          onClick={() => onDeletePenalty(side, penalty.id)}
          disabled={submittingActionKey === deleteKey}
        >
          Delete
        </button>
        <button
          className={`bg-gray-300 text-black rounded px-3 py-1 font-semibold transition-colors ${
            submittingActionKey === endKey ? 'cursor-not-allowed opacity-60' : style.button
          }`}
          onClick={() => onEndPenalty(side, penalty.id)}
          disabled={submittingActionKey === endKey}
        >
          End
        </button>
      </div>
    </div>
  );
};

const PenaltiesSection: React.FC<PenaltiesSectionProps> = ({
  usPenalties,
  themPenalties,
  onDeletePenalty,
  onEndPenalty,
  onAddPenalty,
  submittingActionKey = null,
  redCardMinutes = 5,
}) => (
  <div className="z-10">
    <div className="relative text-2xl font-bold text-center">Yellow/Red Card</div>
    <div className="relative flex w-full justify-between items-stretch gap-4 z-10 pt-3 px-3 h-[80x]">
      <div className="flex-1 flex flex-col items-center">
        <button
          className="w-full font-bold rounded-lg px-3 xs:px-6 py-4 text-center flex justify-center items-center shadow-lg bg-sd-green text-black transition-transform cursor-pointer hover:scale-105"
          onClick={() => onAddPenalty('US')}
        >
          <span className="text-xl sm:text-2xl">US</span>
        </button>
        <div className="flex flex-col gap-2 mt-3 w-full items-start">
          {usPenalties.map((penalty) => (
            <PenaltyCard
              key={penalty.id}
              penalty={penalty}
              side="US"
              redCardMinutes={redCardMinutes}
              submittingActionKey={submittingActionKey}
              onDeletePenalty={onDeletePenalty}
              onEndPenalty={onEndPenalty}
            />
          ))}
        </div>
      </div>
      <div className="flex-1 flex flex-col items-center">
        <button
          className={`w-full font-bold rounded-lg px-3 xs:px-6 py-4 text-center flex justify-center items-center shadow-lg bg-sd-navy text-white transition-transform ${
            submittingActionKey === 'penalty-add-THEM' ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:scale-105'
          }`}
          onClick={() => onAddPenalty('THEM')}
          disabled={submittingActionKey === 'penalty-add-THEM'}
        >
          <span className="text-xl sm:text-2xl">THEM</span>
        </button>
        <div className="flex flex-col gap-2 mt-3 w-full items-end">
          {themPenalties.map((penalty) => (
            <PenaltyCard
              key={penalty.id}
              penalty={penalty}
              side="THEM"
              redCardMinutes={redCardMinutes}
              submittingActionKey={submittingActionKey}
              onDeletePenalty={onDeletePenalty}
              onEndPenalty={onEndPenalty}
            />
          ))}
        </div>
      </div>
    </div>
  </div>
);

export default PenaltiesSection;
